import { useFormContext, useFormField } from '@calendly/form';
import { FieldError } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { AddMiniIcon, ArrowChevronDownMiniIcon } from '@calendly/ui/components/icons';
import { SimpleSelect } from '@calendly/ui/components/select';
import { SelectableChip, SelectableChipGroup } from '@calendly/ui/components/selectable-chip-group';
import { TextInput } from '@calendly/ui/components/text-input';
import { BodyText } from '@calendly/ui/components/typography';
import { useState } from 'react';
import { AddDurationOption } from 'components/event_type_edit/duration/add_duration_option';
import { DurationOptionField } from 'components/event_type_edit/duration/duration_option_field';
import { getDurationFromValues } from 'components/event_type_edit/duration/format_values_to_server_data';
import { getDurationInitialValues } from 'components/event_type_edit/duration/get_initial_values';
import { useEventTypeContext } from 'features/event_type_provider';
import { formatDuration } from 'services/event_types';
const sortDurations = durations => durations.slice().sort((a, b) => a - b);
const getDurationPresets = () => [{
  id: '15',
  label: formatDuration(15, undefined, true)
}, {
  id: '30',
  label: formatDuration(30, undefined, true)
}, {
  id: '45',
  label: formatDuration(45, undefined, true)
}, {
  id: '60',
  label: I18n.t('publisher.event_type_editor_crud.meeting_details.one_hour')
}, {
  id: 'custom',
  label: I18n.t('publisher.event_types.availability.duration_fieldset.custom')
}];
const customRowCss = "c1o7tel2";
const customValueCss = "c1ct0hpa";
const customUnitCss = "c6otb27";
const addMoreButtonCss = "a95cp81";
const durationOptionsListCss = "dxjq5ns";
const addFlowRowCss = "ats0jv8";
const cancelAddFlowButtonCss = "c4wef21";
const unitSelectOptions = () => [{
  id: 'minutes',
  name: I18n.t('time.min_abbr')
}, {
  id: 'hours',
  name: I18n.t('time.hours_abbr.one')
}];
export const CrudDuration = ({
  isLocked
}) => {
  const {
    values,
    setFieldValue
  } = useFormContext();
  const {
    eventDuration
  } = values;
  const {
    eventType
  } = useEventTypeContext();
  const {
    isGroup,
    isMETChild,
    isMETParent
  } = eventType;
  const [customDurationFieldProps, customDurationMeta] = useFormField({
    name: 'customEventDuration'
  });
  const [{
    value: durationOptions
  },, {
    setValue: setDurationOptions
  }] = useFormField({
    name: 'durationOptions'
  });
  const [isAddFlowVisible, setIsAddFlowVisible] = useState(false);
  const showAddFlow = () => setIsAddFlowVisible(true);
  const hideAddFlow = () => setIsAddFlowVisible(false);
  const selectedDurationOptions = durationOptions || [];
  const defaultDuration = getDurationFromValues(values);
  const hasMultipleDurations = selectedDurationOptions.length > 1;
  const canAddMultipleDurations = !isGroup && !isMETChild && !isMETParent && !isLocked;
  const handleSelectionChange = keys => {
    const selected = [...keys][0];
    if (!selected) return;
    setFieldValue('eventDuration', selected);
  };
  const seedDurationOption = duration => {
    setDurationOptions(sortDurations([defaultDuration, duration]));
  };
  const appendDurationOption = duration => {
    setDurationOptions(sortDurations([...selectedDurationOptions, duration]));
  };
  const removeDurationOption = duration => {
    const remaining = sortDurations(selectedDurationOptions.filter(option => option !== duration));
    const isRemovingDefault = defaultDuration === duration;
    if (isRemovingDefault || remaining.length === 1) {
      const newDefaultDuration = remaining.find(option => option > duration) || remaining.slice().pop();
      const {
        eventDuration: nextEventDuration,
        customEventDuration: nextCustomEventDuration,
        customEventDurationType: nextCustomEventDurationType
      } = getDurationInitialValues({
        duration: newDefaultDuration
      });
      setFieldValue('eventDuration', nextEventDuration);
      setFieldValue('customEventDuration', nextCustomEventDuration);
      setFieldValue('customEventDurationType', nextCustomEventDurationType);
    }
    setDurationOptions(remaining);
  };
  if (hasMultipleDurations) {
    return <div data-testid="crud-duration">
        <div className={durationOptionsListCss}>
          {sortDurations(selectedDurationOptions).map(duration => <DurationOptionField key={duration} duration={duration} isDefaultDuration={defaultDuration === duration} onClickMakeDefault={value => setFieldValue('eventDuration', value)} onClickRemove={removeDurationOption} />)}
        </div>
        <AddDurationOption defaultDuration={defaultDuration} updateDurationOptions={appendDurationOption} />
      </div>;
  }
  return <div data-testid="crud-duration">
      <SelectableChipGroup aria-label={I18n.t('publisher.event_types.availability.duration_fieldset.title')} selectionMode="single" disallowEmptySelection selectedKeys={new Set([String(eventDuration)])} onSelectionChange={handleSelectionChange} isDisabled={isLocked}>
        {getDurationPresets().map(({
        id,
        label
      }) => <SelectableChip key={id} id={id} label={label} />)}
      </SelectableChipGroup>

      {eventDuration === 'custom' && <div className={customRowCss}>
          <div className={customValueCss}>
            <TextInput {...customDurationFieldProps} aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_value_label')} inputMode="numeric" disabled={isLocked} />
          </div>
          <div className={customUnitCss}>
            <SimpleSelect name="customEventDurationType" aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_unit_label')} value={values.customEventDurationType || 'minutes'} options={unitSelectOptions()} onChange={e => setFieldValue('customEventDurationType', e.target.value)} disabled={isLocked} />
          </div>
          {customDurationMeta.touched && customDurationMeta.error && <FieldError text={customDurationMeta.error} />}
        </div>}

      {canAddMultipleDurations && (isAddFlowVisible ? <div className={addFlowRowCss}>
            <AddDurationOption defaultDuration={defaultDuration} updateDurationOptions={duration => {
        seedDurationOption(duration);
        hideAddFlow();
      }} />
            <BareButton type="button" className={cancelAddFlowButtonCss} onClick={hideAddFlow}>
              <BodyText size="small" color="standard">
                {I18n.t('publisher.action.cancel')}
              </BodyText>
            </BareButton>
          </div> : <BareButton type="button" className={addMoreButtonCss} onClick={showAddFlow}>
            <AddMiniIcon size="standard" />
            <BodyText size="small" color="standard">
              {I18n.t('publisher.event_type_editor_crud.meeting_details.add_multiple_duration_options')}
            </BodyText>
            <ArrowChevronDownMiniIcon size="standard" />
          </BareButton>)}
    </div>;
};

require("./crud_duration.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./crud_duration.js");